import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, redirect, useChildMatches } from '@tanstack/react-router';
import { Trophy, Users } from 'lucide-react';
import {
  type TournamentSummary,
  fetchPastTournaments,
  fetchUpcomingTournaments,
  registerForTournament,
  withdrawFromTournament,
} from '~/api/tournaments';
import { getSession, useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { formatCountdown, useCountdown } from '~/ui/Countdown';
import { Button } from '~/ui/components/button';

export const Route = createFileRoute('/tournaments')({
  head: () =>
    buildHead({
      title: 'Beat Battle Tournaments',
      description:
        'Browse upcoming and past beat-battle tournaments. Register for the next one, view brackets, ranked seasons by genre.',
      path: '/tournaments',
    }),
  beforeLoad: async ({ location }) => {
    const session = await getSession();
    if (!session) {
      throw redirect({
        to: '/auth/sign-in',
        search: { next: location.pathname },
      });
    }
  },
  component: Tournaments,
});

function Tournaments() {
  // /tournaments/$id (+ /showcase) are nested under this route, so when one is
  // active we must render it through the Outlet instead of the list - without
  // this the detail/bracket page never appears.
  const childMatches = useChildMatches();
  const { data: upcoming, isLoading: upLoading } = useQuery({
    queryKey: ['tournaments', 'upcoming'],
    queryFn: fetchUpcomingTournaments,
    refetchInterval: 30_000,
  });
  const { data: pastRaw, isLoading: pastLoading } = useQuery({
    queryKey: ['tournaments', 'past'],
    queryFn: fetchPastTournaments,
    staleTime: 60_000,
  });
  // Cancelled tournaments (e.g. weekly auto-creations that never reached
  // enough entrants) are noise on the public page - show only ones that ran.
  // Admins still see cancelled ones in the admin panel for cleanup.
  const past = pastRaw?.filter((t) => t.status !== 'cancelled');

  const noUpcoming = !upLoading && (upcoming?.length ?? 0) === 0;
  const noPast = !pastLoading && (past?.length ?? 0) === 0;

  if (childMatches.length > 0) return <Outlet />;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 space-y-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Tournaments</h1>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>Two formats per genre, one registration flow. Honor 70+ required. Free.</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <span className="font-semibold text-foreground">Weekly showcase</span> - auto-created
              every week. You upload one track, voting runs 7 days, top score takes the week. No
              bracket, no second upload. The winner gets the Crowd Favorite + Tournament Winner
              achievements.
            </li>
            <li>
              <span className="font-semibold text-foreground">Bracket tournament</span> - admin-
              scheduled. 3-day showcase opens first (your upload doubles as your round-1 track),
              then single-elimination 1v1 matches. You only make a fresh beat if you advance to
              round 2 onward. Champion gets the Tournament Winner achievement.
            </li>
            <li>
              <span className="font-semibold text-foreground">Withdraw</span> is on the card while
              the open window is running; after it closes you are locked in.
            </li>
          </ul>
        </div>
      </header>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Upcoming</h2>
        {upLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
        {noUpcoming && (
          <div className="rounded-lg border border-dashed border-border p-6 space-y-3">
            <div className="text-sm">
              No upcoming tournaments right now. Weekly tournaments auto-create on a schedule, or an
              admin can spin one up on demand.
            </div>
            <div className="flex gap-3">
              <Button asChild variant="outline" size="sm">
                <Link to="/play">Host a private bracket</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/leaderboard">Leaderboard</Link>
              </Button>
            </div>
          </div>
        )}
        <ul className="space-y-2">
          {upcoming?.map((t) => (
            <li key={t.id}>
              <TournamentCard t={t} kind="upcoming" />
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Past</h2>
        {pastLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
        {noPast && <p className="text-sm text-muted-foreground">No completed tournaments yet.</p>}
        <ul className="space-y-2">
          {past?.map((t) => (
            <li key={t.id}>
              <TournamentCard t={t} kind="past" />
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

function TournamentCard({ t, kind }: { t: TournamentSummary; kind: 'upcoming' | 'past' }) {
  const startsCountdown = useCountdown(kind === 'upcoming' ? t.startsAt : null);
  const regCountdown = useCountdown(
    kind === 'upcoming' && t.status === 'open' ? t.registrationClosesAt : null,
  );
  const showcaseCountdown = useCountdown(
    kind === 'upcoming' && t.status === 'showcase' ? (t.showcaseEndsAt ?? null) : null,
  );

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3 hover:border-primary/40 transition-colors">
      <div className="flex flex-wrap items-start gap-3">
        <Trophy className="size-5 text-primary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <Link
            to="/tournaments/$id"
            params={{ id: t.id }}
            className="font-semibold hover:underline"
          >
            {t.name}
          </Link>
          <div className="text-xs text-muted-foreground mt-0.5">
            {t.genreName} · {t.entrantCount}/{t.maxEntrants} entrants
          </div>
          {!t.bracketEnabled && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-violet-400 mt-0.5">
              weekly showcase - no bracket - single upload
            </div>
          )}
        </div>
        <StatusPill status={t.status} />
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
        {kind === 'upcoming' && t.status === 'open' && regCountdown !== null && (
          <span className="text-amber-300">
            registration closes in{' '}
            <span className="font-mono">{formatCountdown(regCountdown)}</span>
          </span>
        )}
        {kind === 'upcoming' && t.status === 'showcase' && showcaseCountdown !== null && (
          <span className="text-violet-300">
            showcase closes in{' '}
            <span className="font-mono">{formatCountdown(showcaseCountdown)}</span>
          </span>
        )}
        {kind === 'upcoming' && startsCountdown !== null && (
          <span className="text-muted-foreground">
            starts in <span className="font-mono">{formatCountdown(startsCountdown)}</span>
          </span>
        )}
        {kind === 'past' && (
          <span className="text-muted-foreground">
            played {new Date(t.startsAt).toLocaleDateString()}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-muted-foreground">
          <Users className="size-3" /> {t.entrantCount}/{t.maxEntrants}
        </span>
      </div>
      {kind === 'upcoming' && t.status === 'open' && <RegisterButtons t={t} />}
      {kind === 'upcoming' && t.status === 'showcase' && (
        <Button asChild size="sm" variant="outline">
          <Link to="/tournaments/$id/showcase" params={{ id: t.id }}>
            Listen + vote
            {showcaseCountdown !== null && (
              <span className="ml-1 font-mono text-xs text-muted-foreground">
                ({formatCountdown(showcaseCountdown)} left)
              </span>
            )}
          </Link>
        </Button>
      )}
    </div>
  );
}

function RegisterButtons({ t }: { t: TournamentSummary }) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const registerMut = useMutation({
    mutationFn: () => registerForTournament(t.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tournaments'] }),
  });
  const withdrawMut = useMutation({
    mutationFn: () => withdrawFromTournament(t.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tournaments'] }),
  });
  const pending = registerMut.isPending || withdrawMut.isPending;

  if (!session) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to="/auth/sign-in">Sign in to register</Link>
      </Button>
    );
  }
  if (t.registered) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-emerald-400">You're in.</span>
        <Button size="sm" variant="ghost" disabled={pending} onClick={() => withdrawMut.mutate()}>
          {pending ? '...' : 'Withdraw'}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Button size="sm" disabled={pending} onClick={() => registerMut.mutate()}>
        {pending ? 'Registering...' : 'Register'}
      </Button>
      {registerMut.isError && (
        <p className="text-xs text-destructive">{String(registerMut.error)}</p>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: TournamentSummary['status'] }) {
  const styles: Record<TournamentSummary['status'], string> = {
    open: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    showcase: 'border-violet-500/40 bg-violet-500/10 text-violet-300',
    starting: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    in_progress: 'border-primary/40 bg-primary/10 text-primary',
    finished: 'border-border bg-muted/40 text-muted-foreground',
    cancelled: 'border-border bg-muted/40 text-muted-foreground line-through',
  };
  const label: Record<TournamentSummary['status'], string> = {
    open: 'open',
    showcase: 'showcase',
    starting: 'starting',
    in_progress: 'in progress',
    finished: 'finished',
    cancelled: 'cancelled',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${styles[status]}`}
    >
      {label[status]}
    </span>
  );
}
